// DemoSection — shared "¿Quieres probar la app?" autosign section.
//
// Jaime directive 2026-10-12: this section previously lived only inside
// LoginPage (commit b4e40db). Extracted here so the public landing renders
// the SAME section directly on the main site (reuse — the LoginPage instance
// is NOT removed). Both instances hit the identical LIVE contract:
//   POST /api/demo/autosign { userType: admin|propietario|residente|empleado }
//     -> login-shaped payload { data: { user, roles, tokens, token } }
// and land on the role's dashboard with the demo condo context — a NORMAL
// session, same auth flow as a regular login.
//
// Truth-only rule (Jaime standing directive): no fabricated numbers here and
// none may be added — usage/testimonial claims stay governed by
// SOCIAL_PROOF_ENABLED (constants/featureFlags.ts).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export type DemoRole = 'Admin' | 'Propietario' | 'Residente' | 'Empleado';

interface DemoSectionProps {
  /** Wrapper classes. Defaults to the original LoginPage column styling. */
  className?: string;
  /** Anchor id so /login#demo and on-page CTAs keep working. Default 'demo'. */
  id?: string;
  /**
   * Optional error delegate: when provided (LoginPage), demo errors surface in
   * that page's existing shared error box instead of an inline box — identical
   * behavior to the pre-extraction inline implementation.
   */
  onError?: (message: string) => void;
}

export default function DemoSection({
  className = 'mt-6 border-t border-gray-100 pt-5',
  id = 'demo',
  onError,
}: DemoSectionProps) {
  const navigate = useNavigate();
  const { completeLoginWithPayload } = useAuth();
  // Busy state holds the label of the chip in flight so only that chip shows
  // its progress.
  const [demoBusy, setDemoBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleError = (message: string) => {
    if (onError) onError(message);
    else setError(message);
  };

  const handleDemoAutosign = async (label: DemoRole) => {
    handleError('');
    setDemoBusy(label);
    try {
      // LIVE backend contract (api/src/modules/demo): POST /api/demo/autosign
      // { userType: admin|propietario|residente|empleado } -> login-shaped
      // payload { data: { user, roles, tokens, token } } — a NORMAL session.
      const res = await fetch('/api/demo/autosign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userType: label.toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        handleError(data.error || 'No se pudo iniciar la sesión de demostración. Intenta de nuevo.');
        return;
      }
      // Same auth flow as a normal login: persist tokens + user payload.
      const okLogin = await completeLoginWithPayload(data);
      if (!okLogin) { handleError('No se pudo establecer la sesión de demostración. Intenta de nuevo.'); return; }
      // Land on the role's dashboard with the demo condo context.
      const role = data?.data?.user?.role;
      const destination = role === 'super_admin'
        ? '/super-admin'
        : role === 'admin' || role === 'sub_admin'
          ? '/admin'
          : role === 'security'
            ? '/security-dashboard'
            : '/home/dashboard';
      navigate(destination, { replace: true });
    } catch {
      handleError('No se pudo iniciar la sesión de demostración. Intenta de nuevo.');
    } finally { setDemoBusy(null); }
  };

  return (
    <div id={id} className={className}>
      <p className="mb-3 text-center text-sm font-semibold text-gray-700">¿Quieres probar la app?</p>
      {!onError && error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-center">
          <p className="text-red-700 text-sm font-medium">{error}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {(['Admin', 'Propietario', 'Residente', 'Empleado'] as DemoRole[]).map((label) => (
          <button
            key={label}
            type="button"
            disabled={!!demoBusy}
            onClick={() => handleDemoAutosign(label)}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-60 hover:border-[#36CCD8]/40 hover:text-[#36CCD8]"
          >
            {demoBusy === label ? 'Entrando…' : `Probar como ${label}`}
          </button>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-gray-400">Explora Vecindus con datos de ejemplo</p>
    </div>
  );
}
